import React, { useCallback, useEffect, useRef, useState } from "react";
import { Box, CircularProgress } from "@mui/material";
import { useSelector, useDispatch } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { useBackroute } from "../../contexts/BackrouteContext";
import { toast } from "react-toastify";
import CustomStepper from "../../v2/MUI_custom_component/Stepper";
import { getBuyerRolesAction } from "../../v2/Onboarding/auth/action";
import { fetchDigiLockerData } from "./API/Action";
import RoleStep from "./Components/RoleStep";
import UserDetailsStep from "./Components/UserDetailsStep";
import UserAddressStep from "./Components/OnboardBuyerAddress/UserAddressStep";
import FirmDetailsStep from "./Components/FirmDetailsStep";
import HumbeeModal from "../../v2/MUI_custom_component/Modal/HumbeeModal";
import DataLostSvg from "../Illustrration/components/DataLostSvg";
import { buildKycFromKycResponse } from "./Redux/kycDetailsBuilders";

export const OnboardBuyer = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { updateBackroute, resetBackroute } = useBackroute();
  const activeStep = useSelector(
    (state) => state.onboardBuyerReducer.activeStep,
  );
  const selectedRole = useSelector(
    (state) => state.onboardBuyerReducer.selectedRole,
  );
  const selectedSupplier = useSelector(
    (state) => state.entityReducer.selectedSupplier,
  );
  const vcpRoles = useSelector((state) => state.onboardBuyerReducer.vcpRoles);
  const isInfluencerRole = selectedRole?.region_role_id == null;

  const location = useLocation();
  const {
    presetInfluencer,
    preselectFirmRoleId,
    mobileNumber: prefillMobile,
  } = location.state || {};

  useEffect(() => {
    if (prefillMobile) {
      dispatch({
        type: "SET_USER_DETAIL_FIELD",
        payload: { field: "mobileNumber", value: prefillMobile },
      });
    }
    if (presetInfluencer) {
      dispatch({ type: "TOGGLE_INFLUENCER", payload: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (presetInfluencer || !preselectFirmRoleId || !vcpRoles?.length) return;
    const match = vcpRoles.find(
      (r) => r.firm_role_id === preselectFirmRoleId,
    );
    if (match) dispatch({ type: "SELECT_VCP_ROLE", payload: match });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vcpRoles]);
  
  const STEPS = [
    { id: "role", label: "Role" },
    { id: "user-details", label: "User Details" },
    {
      id: "user-address",
      label: isInfluencerRole ? "User Address" : "Firm Details",
    },
  ];

  const [digiLockerLoading, setDigiLockerLoading] = useState(false);
  const [addressPreview, setAddressPreview] = useState(false);

  const backStateRef = useRef({ screen: "role", hasData: false, back: null });
  const setBackState = useCallback((s) => {
    backStateRef.current = s || { screen: "role", hasData: false, back: null };
  }, []);

  const [backConfirm, setBackConfirm] = useState(false);
  const pendingBackRef = useRef(null);

  const requestBackRef = useRef(() => {});
  requestBackRef.current = () => {
    const { hasData, back } = backStateRef.current || {};
    const doBack = typeof back === "function" ? back : () => navigate(-1);
    if (hasData) {
      pendingBackRef.current = doBack;
      setBackConfirm(true);
    } else {
      doBack();
    }
  };
  const requestBackWith = useCallback(
    (backFn) => {
      const { hasData } = backStateRef.current || {};
      const doBack = typeof backFn === "function" ? backFn : () => navigate(-1);
      if (hasData) {
        pendingBackRef.current = doBack;
        setBackConfirm(true);
      } else {
        doBack();
      }
    },
    [navigate],
  );

  useEffect(() => {
    updateBackroute({ customHandler: () => requestBackRef.current() });
    return () => resetBackroute();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const returnTransactionId = new URLSearchParams(window.location.search).get(
    "transaction_id",
  );

  useEffect(() => {
    return () => {
      dispatch({ type: "RESET_ONBOARD_BUYER" });
      dispatch({ type: "RESET_OPERATIONAL_DETAILS" });
      dispatch({ type: "RESET_PAN_DETAILS" });
      dispatch({ type: "SET_SHOW_GST_INPUT", payload: false });
      dispatch({ type: "SET_GST_NUMBER", payload: null });
      dispatch({ type: "SET_GST_DETAILS", payload: null });
    };
  }, [dispatch]);

  const [showResumeModal, setShowResumeModal] = useState(
    () => (activeStep > 0 || selectedRole != null) && !returnTransactionId,
  );

  useEffect(() => {
    if (!returnTransactionId) return;
    const pocket = JSON.parse(
      sessionStorage.getItem("onboard_buyer_digilocker") || "{}",
    );
    sessionStorage.removeItem("onboard_buyer_digilocker");
    window.history.replaceState({}, "", window.location.pathname);
    setDigiLockerLoading(true);
    fetchDigiLockerData(returnTransactionId).then((res) => {
      setDigiLockerLoading(false);
      if (!res?.error) {
        dispatch({
          type: "SET_KYC_DETAILS",
          payload: buildKycFromKycResponse(
            { ...res.data, session_id: pocket.session_id || returnTransactionId },
            "DIGILOCKER",
          ),
        });
        dispatch({ type: "SET_KYC_METHOD", payload: "DIGILOCKER" });
      } else toast.error(res?.message || "Failed to fetch DigiLocker data");
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchBuyerRoles = useCallback(
    async (supplierId) => {
      dispatch({ type: "SET_BUYER_ROLES_LOADING", payload: true });
      const { data, error, message } = await getBuyerRolesAction(
        Number(supplierId),
        false,
        true,
      );
      dispatch({ type: "SET_BUYER_ROLES_LOADING", payload: false });

      if (error) {
        toast.error(message || "Failed to fetch buyer roles");
        return;
      }

      const roles = data?.roles || [];
      dispatch({
        type: "SET_BUYER_ROLES_DATA",
        payload: {
          manufacturer: {
            manufacturer_id: data?.manufacturer_id,
            manufacturer_name: data?.manufacturer_name,
            url: data?.url,
          },
          vcpRoles: roles.filter((r) => r.region_role_id !== null),
          influencerRoles: roles.filter((r) => r.region_role_id === null),
        },
      });
    },
    [dispatch],
  );

  useEffect(() => {
    if (selectedSupplier) fetchBuyerRoles(selectedSupplier);
  }, [selectedSupplier, fetchBuyerRoles]);

  const handleResume = () => setShowResumeModal(false);

  const handleStartOver = () => {
    dispatch({ type: "RESET_ONBOARD_BUYER" });
    dispatch({ type: "RESET_OPERATIONAL_DETAILS" });
    dispatch({ type: "RESET_PAN_DETAILS" });
    dispatch({ type: "SET_SHOW_GST_INPUT", payload: false });
    dispatch({ type: "SET_GST_NUMBER", payload: null });
    dispatch({ type: "SET_GST_DETAILS", payload: null });
    if (selectedSupplier) fetchBuyerRoles(selectedSupplier);
    setShowResumeModal(false);
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: { xs: 4, md: 8 },
        width: "100%",
        p: 2,
      }}>
      {!addressPreview && (
        <CustomStepper
          steps={STEPS}
          activeStep={activeStep}
          orientation="horizontal"
          showFlex={false}
          type="horizontal-left"
          size="small"
          onStepClick={(stepIndex) =>
            dispatch({ type: "SET_ONBOARD_ACTIVE_STEP", payload: stepIndex })
          }
        />
      )}
      {activeStep === 0 && <RoleStep onBackState={setBackState} />}
      {activeStep === 1 &&
        (digiLockerLoading ? (
          <Box sx={{ py: 8 }}>
            <CircularProgress />
          </Box>
        ) : (
          <UserDetailsStep onBackState={setBackState} />
        ))}
      {activeStep === 2 &&
        (isInfluencerRole ? (
          <UserAddressStep
            onPreviewChange={setAddressPreview}
            onRequestCancel={() => setShowResumeModal(true)}
            onBackState={setBackState}
            requestBackWith={requestBackWith}
          />
        ) : (
          <FirmDetailsStep
            onPreviewChange={setAddressPreview}
            onRequestCancel={() => setShowResumeModal(true)}
            onBackState={setBackState}
            requestBackWith={requestBackWith}
          />
        ))}
      <HumbeeModal
        open={showResumeModal}
        onClose={handleResume}
        onConfirm={handleStartOver}
        title="Are you sure you want to cancel?"
        subtitle="All data would be lost."
        confirmLabel="Yes"
        cancelLabel="No"
        illustration={<DataLostSvg />}
      />
      <HumbeeModal
        open={backConfirm}
        onClose={() => setBackConfirm(false)}
        onConfirm={() => {
          setBackConfirm(false);
          const fn = pendingBackRef.current;
          pendingBackRef.current = null;
          if (typeof fn === "function") fn();
        }}
        title="Are you sure you want to go back?"
        subtitle="The details on this screen would be lost."
        confirmLabel="Yes"
        cancelLabel="No"
        illustration={<DataLostSvg />}
      />
    </Box>
  );
};
